import React, { Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import {
  getOrganizerById,
  getBranchesByOrganizerId,
  MOCK_BRANCHES,
  Organizer,
  Branch
} from '@/data/mockTableBookingData';
import { apiService } from '@/services/api';
import Pagination from '@/components/common/Pagination/Pagination';
import { branchTypeTheme } from '@/utils/branchTypeTheme';
import styles from './OrganizerDetail.module.scss';

const BRANCH_PAGE_SIZE = 8;

type BranchRow = Branch & { categoryCode?: string | null; categoryLabel?: string | null };

interface OrganizerDetailState {
  organizer: Organizer | null;
  branches: BranchRow[];
  isLoading: boolean;
  isLoadingBranches: boolean;
  error: string | null;
  activeView: 'options' | 'branches' | 'tournaments';
  branchPage: number;
  selectedCategory: string | null; // null = All categories
}

function withNavigation(WrappedComponent: any) {
  return function WithNavigationComponent(props: any) {
    const navigate = useNavigate();
    const params = useParams();
    return <WrappedComponent {...props} navigate={navigate} params={params} />;
  };
}

class OrganizerDetail extends Component<any, OrganizerDetailState> {
  constructor(props: any) {
    super(props);
    this.state = {
      organizer: null,
      branches: [],
      isLoading: true,
      isLoadingBranches: true,
      error: null,
      activeView: 'branches', // clicking an organizer lands straight on its branch list
      branchPage: 1,
      selectedCategory: null
    };
  }

  componentDidMount() {
    // Start at top when opening organizer detail (avoids showing bottom of page)
    this.scrollToTop();

    const organizerId = this.props.params?.organizerId;
    if (organizerId) {
      this.loadOrganizerData(organizerId);
      this.loadBranches(organizerId);
    }
  }

  componentDidUpdate(prevProps: any, prevState: OrganizerDetailState) {
    // When switching to branches view, scroll to top so user sees "Select a Branch" and the list start
    if (prevState.activeView !== this.state.activeView && this.state.activeView === 'branches') {
      this.scrollToTop();
      return;
    }
    // When branches finish loading while on branches view, ensure we're at top (content just appeared)
    if (
      this.state.activeView === 'branches' &&
      prevState.isLoadingBranches === true &&
      this.state.isLoadingBranches === false
    ) {
      this.scrollToTop();
    }
  }

  scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  loadOrganizerData = async (organizerId: string) => {
    this.setState({ isLoading: true, error: null });
    
    try {
      // Try to load organizer details from API
      const apiCall = apiService.getOrganizerDetails(organizerId);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      let response: any;
      try {
        response = await Promise.race([apiCall, timeoutPromise]) as any;
      } catch (timeoutError) {
        console.warn('[OrganizerDetail] API call timed out, using mock data');
        throw timeoutError;
      }
      
      console.log('[OrganizerDetail] Organizer details API response:', response);

      if (response) {
        const payload: any = response as any;
        const organizerData: any = (payload && (payload.data ?? payload)) ?? {};
        
        // Map API response to Organizer interface (API returns organizerLogoUrl in data object)
        const name = organizerData.organizationName || organizerData.organization_name || organizerData.title || organizerData.name || organizerData.contact_person_name || 'Organizer';
        const city = organizerData.city || organizerData.location?.city || 'Unknown';
        const state = organizerData.state || organizerData.location?.state || '';
        const country = organizerData.country || organizerData.location?.country || '';
        const addressParts = [organizerData.address, city, state, country].filter(Boolean);
        const address = addressParts.length > 0 ? addressParts.join(', ') : 'Address not available';
        const logo = organizerData.organizerLogoUrl || organizerData.organizer_logo_url || organizerData.profilePicUrl || organizerData.logoUrl || organizerData.logo_url || organizerData.image || undefined;
        const image = organizerData.organizerLogoUrl || organizerData.organizer_logo_url || organizerData.profilePicUrl || organizerData.logoUrl || organizerData.coverImageUrl || organizerData.cover_image_url || organizerData.image || 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&h=300&fit=crop&auto=format';
        
        const mappedOrganizer: Organizer = {
          id: organizerId,
          name,
          logo,
          // Only surface real, API-provided values. Missing fields stay empty (''/0)
          // so the UI can hide them rather than showing invented placeholder data.
          description: organizerData.description || organizerData.about || organizerData.bio || '',
          address,
          city,
          phone: organizerData.phone || organizerData.contact || organizerData.contact_number || '',
          email: organizerData.email || '',
          rating: Number(organizerData.rating) || 0,
          reviewCount: Number(organizerData.review_count || organizerData.reviewCount || organizerData.total_reviews || 0),
          branchCount: Number(organizerData.branch_count || organizerData.branchCount || organizerData.total_branches || 0),
          status: (organizerData.status === 'active' || organizerData.is_active || organizerData.isActive) ? 'active' : 'inactive',
          image
        };

        console.log('[OrganizerDetail] Mapped organizer:', mappedOrganizer);
        this.setState({
          organizer: mappedOrganizer,
          isLoading: false
        });
      } else {
        // Fallback to mock data if no response
        const organizer = getOrganizerById(organizerId);
        this.setState({
          organizer: organizer || null,
          isLoading: false
        });
      }
    } catch (error: any) {
      console.error('[OrganizerDetail] Failed to load organizer details:', error);
      // Fallback to mock data on error
      const organizer = getOrganizerById(organizerId);
      this.setState({
        organizer: organizer || null,
        isLoading: false,
        error: error?.message?.includes('timeout') 
          ? 'Request timed out. Showing sample data if available.' 
          : 'Failed to load organizer details. Showing sample data if available.'
      });
    }
  };


  loadBranches = async (organizerId: string) => {
    this.setState({ isLoadingBranches: true, error: null });
    
    try {
      // Add timeout to prevent hanging
      const apiCall = apiService.getOrganizerBranches(organizerId);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      let response: any;
      try {
        response = await Promise.race([apiCall, timeoutPromise]) as any;
      } catch (timeoutError) {
        console.warn('[OrganizerDetail] API call timed out or failed, using mock data');
        throw timeoutError;
      }
      
      console.log('[OrganizerDetail] Branches API response:', response);

      if (response) {
        const payload: any = response as any;
        // Extract branches from response.data.branches or response.data
        const container: any = (payload && (payload.data ?? payload)) ?? {};
        let rawBranches: any = container.branches || container.data || container;

        if (!Array.isArray(rawBranches)) {
          const firstArray = Object.values(container).find((v: any) => Array.isArray(v));
          rawBranches = Array.isArray(firstArray) ? firstArray : [];
        }

        if (!Array.isArray(rawBranches)) {
          console.warn('[OrganizerDetail] Unexpected branches payload shape; received:', payload);
          // Fallback to mock data
          this.setState({ 
            branches: MOCK_BRANCHES.filter(b => b.organizerId === organizerId), 
            isLoadingBranches: false 
          });
          return;
        }

        // Map API response to Branch interface
        const mapped: Branch[] = rawBranches.map((branch: any, idx: number) => {
          const address = branch.address || 'Address not available';
          const city = branch.city || '';
          const state = branch.state || '';
          const country = branch.country || '';
          const fullAddress = [address, city, state, country].filter(Boolean).join(', ') || address;
          
          return {
            id: branch.id || branch._id || `branch-${idx + 1}`,
            organizerId: organizerId,
            name: branch.name || `Branch ${idx + 1}`,
            address: fullAddress,
            phone: branch.phone || branch.contact || branch.contact_number || '',
            email: branch.email || '',
            status: (branch.isActive === true || branch.status === 'active' || branch.is_active) ? 'active' : 'inactive',
            tableCount: Number(branch.tableCount || branch.table_count || branch.totalTables || branch.total_tables || 0),
            availableTables: Number(branch.availableTables || branch.available_tables || branch.availableTableCount || 0),
            images: branch.images || branch.imageUrls || (branch.image ? [branch.image] : undefined),
            // Additional fields from API
            operatingHours: branch.operatingHours || branch.operating_hours,
            city: city,
            state: state,
            country: country,
            categoryCode: branch.branchType?.code ?? null,
            categoryLabel: branch.branchType?.label ?? null
          };
        });

        console.log('[OrganizerDetail] Mapped branches count:', mapped.length, mapped);
        this.setState({ 
          branches: mapped.length > 0 ? mapped : MOCK_BRANCHES.filter(b => b.organizerId === organizerId), 
          isLoadingBranches: false 
        });
      } else {
        // Fallback to mock data if no response
        this.setState({ 
          branches: MOCK_BRANCHES.filter(b => b.organizerId === organizerId), 
          isLoadingBranches: false 
        });
      }
    } catch (error: any) {
      console.error('[OrganizerDetail] Failed to load branches:', error);
      // Fallback to mock data on error
      const organizerId = this.props.params?.organizerId;
      this.setState({ 
        branches: MOCK_BRANCHES.filter(b => b.organizerId === organizerId), 
        isLoadingBranches: false,
        error: error?.message?.includes('timeout') 
          ? 'Request timed out. Showing sample data.' 
          : 'Failed to load branches. Showing sample data.'
      });
    }
  };

  handleBranchClick = (branchId: string) => {
    const organizerId = this.props.params?.organizerId;
    // Go to the branch hub (choose a segment: tables / rooms / tournaments)
    this.props.navigate(`/customer/organizers/${organizerId}/branches/${branchId}`);
  };

  handleBackClick = () => {
    this.props.navigate('/customer/organizers');
  };

  renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i} className={styles.star}>★</span>);
    }

    if (hasHalfStar) {
      stars.push(<span key="half" className={styles.starHalf}>★</span>);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<span key={`empty-${i}`} className={styles.starEmpty}>★</span>);
    }

    return stars;
  };

  render() {
    const { organizer, branches, isLoading, isLoadingBranches, error, branchPage, selectedCategory } = this.state;

    // Category catalog for this organizer (distinct branch types across its branches) + counts
    const catMap = new Map<string, { code: string; label: string; count: number }>();
    branches.forEach((b) => {
      const code = b.categoryCode || 'other';
      const label = b.categoryLabel || 'Other';
      const cur = catMap.get(code) || { code, label, count: 0 };
      cur.count += 1;
      catMap.set(code, cur);
    });
    const categories = Array.from(catMap.values()).sort((a, b) => a.label.localeCompare(b.label));

    // Insights
    const totalBranches = branches.length;
    const categoryCount = categories.length;
    const activeBranches = branches.filter((b) => b.status === 'active').length;

    // Filter by selected category, then paginate
    const filteredBranches = selectedCategory
      ? branches.filter((b) => (b.categoryCode || 'other') === selectedCategory)
      : branches;
    const branchStart = (branchPage - 1) * BRANCH_PAGE_SIZE;
    const pagedBranches = filteredBranches.slice(branchStart, branchStart + BRANCH_PAGE_SIZE);

    if (isLoading) {
      return (
        <>
          <DashboardHeader />
          <div className={styles.organizerDetailContainer}>
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading organizer details...</p>
            </div>
          </div>
          <Footer />
        </>
      );
    }

    if (!organizer) {
      return (
        <>
          <DashboardHeader />
          <div className={styles.organizerDetailContainer}>
            <div className={styles.errorState}>
              <h2>Organizer not found</h2>
              <button onClick={this.handleBackClick} className={styles.backButton}>
                Back to Organizers
              </button>
            </div>
          </div>
          <Footer />
        </>
      );
    }

    return (
      <>
        <DashboardHeader />
        <div className={styles.organizerDetailContainer}>
          <div className={styles.organizerDetailContent}>
            {/* Back Button */}
            <button onClick={this.handleBackClick} className={styles.backButton}>
              ← Back to Organizers
            </button>

            {/* Organizer Header */}
            <div className={styles.organizerHeader}>
              <div className={styles.organizerLogo}>
                <img
                  src={organizer.logo || organizer.image || 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=200&h=200&fit=crop&auto=format'}
                  alt={organizer.name}
                />
              </div>
              <div className={styles.organizerHeaderInfo}>
                <h1 className={styles.organizerName}>{organizer.name}</h1>
                {organizer.description && (
                  <p className={styles.organizerDescription}>{organizer.description}</p>
                )}

                {/* Only show a rating when the API provides a real one — never a fabricated score. */}
                {organizer.rating > 0 && (
                  <div className={styles.organizerRating}>
                    <div className={styles.starsContainer}>
                      {this.renderStars(organizer.rating)}
                    </div>
                    <span className={styles.ratingValue}>{organizer.rating.toFixed(1)}</span>
                    {organizer.reviewCount > 0 && (
                      <span className={styles.reviewCount}>({organizer.reviewCount} reviews)</span>
                    )}
                  </div>
                )}

                <div className={styles.organizerContact}>
                  {organizer.address && (
                    <div className={styles.contactItem}>
                      <span className={styles.contactIcon}>📍</span>
                      <span>{organizer.address}{organizer.city ? `, ${organizer.city}` : ''}</span>
                    </div>
                  )}
                  {organizer.phone && (
                    <div className={styles.contactItem}>
                      <span className={styles.contactIcon}>📞</span>
                      <span>{organizer.phone}</span>
                    </div>
                  )}
                  {organizer.email && (
                    <div className={styles.contactItem}>
                      <span className={styles.contactIcon}>✉️</span>
                      <span>{organizer.email}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Insights strip */}
            {!isLoadingBranches && totalBranches > 0 && (
              <div className={styles.insightsStrip}>
                <div className={styles.insightCard}>
                  <span className={styles.insightValue}>{totalBranches}</span>
                  <span className={styles.insightLabel}>Branches</span>
                </div>
                <div className={styles.insightCard}>
                  <span className={styles.insightValue}>{categoryCount}</span>
                  <span className={styles.insightLabel}>{categoryCount === 1 ? 'Category' : 'Categories'}</span>
                </div>
                <div className={styles.insightCard}>
                  <span className={styles.insightValue}>{activeBranches}</span>
                  <span className={styles.insightLabel}>Active now</span>
                </div>
              </div>
            )}

            {/* Branches Section — compact list, click a branch to choose a service */}
            <div className={styles.branchesSection}>
              <h2 className={styles.sectionTitle}>Select a Branch</h2>
              <p className={styles.sectionSubtitle}>
                Choose a branch to see its tables, rooms and tournaments
              </p>

              {/* Category filter buttons */}
              {!isLoadingBranches && categories.length > 0 && (
                <div className={styles.categoryChips}>
                  <button
                    className={`${styles.categoryChip} ${selectedCategory === null ? styles.categoryChipActive : ''}`}
                    onClick={() => this.setState({ selectedCategory: null, branchPage: 1 })}
                  >
                    All <span className={styles.chipCount}>{totalBranches}</span>
                  </button>
                  {categories.map((c) => (
                    <button
                      key={c.code}
                      className={`${styles.categoryChip} ${selectedCategory === c.code ? styles.categoryChipActive : ''}`}
                      onClick={() => this.setState({ selectedCategory: c.code, branchPage: 1 })}
                    >
                      {c.label} <span className={styles.chipCount}>{c.count}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className={styles.errorMessage}>
                  <span>⚠️ {error}</span>
                </div>
              )}

              {/* Loading State */}
              {isLoadingBranches ? (
                <div className={styles.loadingContainer}>
                  <div className={styles.loadingSpinner}></div>
                  <p>Loading branches...</p>
                </div>
              ) : branches.length === 0 ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyStateIcon}>🏢</div>
                  <h3>No branches available</h3>
                  <p>This organizer doesn't have any active branches at the moment.</p>
                </div>
              ) : (
                <>
                  <div className={styles.branchesCount}>
                    {filteredBranches.length} {filteredBranches.length === 1 ? 'branch' : 'branches'}{selectedCategory ? ` in ${catMap.get(selectedCategory)?.label || 'this category'}` : ' found'}
                  </div>
                  <div className={styles.branchesGrid}>
                    {pagedBranches.map(branch => (
                      <div
                        key={branch.id}
                        className={styles.branchCard}
                        onClick={() => this.handleBranchClick(branch.id)}
                      >
                        {branch.images && branch.images.length > 0 ? (
                          <div className={styles.branchImage}>
                            <img src={branch.images[0]} alt={branch.name} />
                          </div>
                        ) : (
                          <div className={styles.branchImagePlaceholder}>
                            <span className={styles.placeholderIcon}>🏢</span>
                          </div>
                        )}

                        <div className={styles.branchInfo}>
                          <div className={styles.branchTopRow}>
                            <h3 className={styles.branchName}>{branch.name}</h3>
                            <span className={`${styles.availabilityBadge} ${
                              branch.status === 'active' ? styles.badgeActive : styles.badgeInactive
                            }`}>
                              {branch.status === 'active' ? 'Active' : 'Closed'}
                            </span>
                          </div>
                          <div className={styles.branchDetailItem}>
                            <span className={styles.detailIcon}>📍</span>
                            <span className={styles.detailText}>{branch.address}</span>
                          </div>
                          <div className={styles.branchMetaRow}>
                            {branch.categoryLabel && (() => { const th = branchTypeTheme({ code: branch.categoryCode, label: branch.categoryLabel }); return <span className={styles.categoryTag} style={{ background: `${th.accent}1f`, color: th.accent }}>{th.emoji} {branch.categoryLabel}</span>; })()}
                            {branch.tableCount > 0 && <span className={styles.branchMetaText}>🪑 {branch.tableCount} tables</span>}
                          </div>
                        </div>
                        <span className={styles.branchChevron}>›</span>
                      </div>
                    ))}
                  </div>
                  <Pagination
                    page={branchPage}
                    total={filteredBranches.length}
                    pageSize={BRANCH_PAGE_SIZE}
                    onChange={(p) => { this.setState({ branchPage: p }); this.scrollToTop(); }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
}

export default withNavigation(OrganizerDetail);

